import { useCallback, useState, type JSX } from "react";
import {
  DocumentAddRegular,
  ClockRegular,
  OpenRegular,
  ChevronRightRegular,
  TableRegular,
  PeopleTeamRegular,
} from "@fluentui/react-icons";
import { TabSectionHeader } from "@/components/Workflow/TabSectionHeader";
import { FluentSpinner } from "@/components/fluent-ui";
import { getCreateDealDisplay, openCreateDealDialog } from "@/services/outlook/create-deal-dialog";
import { getWeeklyHoursDisplay, openWeeklyHoursDialog } from "@/services/outlook/weekly-hours-dialog";
import { getDealTrackerDisplay, openDealTrackerDialog } from "@/services/outlook/deal-tracker-dialog";
import { getWorkflowAccess } from "@/services/auth/role-privileges";
import { HoursApi } from "@/services/api/hours-api";
import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/services/auth/use-auth";
import { resolveLaunchUrl } from "@/utils/launch-url";
import { CreateDeal } from "./CreateDeal";
import { WeeklyHours } from "./WeeklyHours";
import { DealTrackerHome, resolveDealTrackerRole } from "./DealTracker";
import "../shared.scss";
import "./QuickActions.scss";

interface QuickAction {
  id: string;
  title: string;
  desc: string;
  icon: JSX.Element;
  action: "create-deal" | "weekly-hours" | "deal-tracker" | "launch-web";
}

const LAUNCH_TRIYO: QuickAction = {
  id: "launch-triyo",
  title: "Launch TRIYO",
  desc: "Open the full TRIYO web application",
  icon: <OpenRegular />,
  action: "launch-web",
};

const DEAL_TRACKER_BANKER: QuickAction = {
  id: "deal-tracker",
  title: "Update Your Deal Tracker",
  desc: "Edit, add and delete your M&A deal rows",
  icon: <TableRegular />,
  action: "deal-tracker",
};

const DEAL_TRACKER_STAFFER: QuickAction = {
  id: "deal-tracker",
  title: "View & Update Banker Deal Trackers",
  desc: "Review your team's deals and edit on their behalf",
  icon: <PeopleTeamRegular />,
  action: "deal-tracker",
};

const ACTIONS: QuickAction[] = [
  {
    id: "create-deal",
    title: "Create Deal",
    desc: "Set up a new Deal without leaving Outlook",
    icon: <DocumentAddRegular />,
    action: "create-deal",
  },
  {
    id: "weekly-hours",
    title: "Enter Weekly Hours",
    desc: "Open the TRIYO form for your eligible Deals",
    icon: <ClockRegular />,
    action: "weekly-hours",
  },
  LAUNCH_TRIYO,
];

const VIEW_TITLES: Record<string, string> = {
  "create-deal": "Create Deal",
  "weekly-hours": "Enter Weekly Hours",
};

export function QuickActions(): JSX.Element {
  const { user } = useAuth();
  const [view, setView] = useState<"list" | "create-deal" | "weekly-hours" | "deal-tracker">("list");
  const [notice, setNotice] = useState("");
  const { canCreateDeals } = getWorkflowAccess(user);

  // An M&A Banker's whole add-in job is their own Deal Tracker, and a Staffer's is
  // their team's, so either way the shortcut list is that plus Launch TRIYO rather
  // than the general Quick Actions (TAD-6037 / TAD-6072). getUserSubDepartments
  // answers [] rather than failing for accounts with no dashboard at all.
  const subDepartmentFetcher = useCallback(() => HoursApi.getUserSubDepartments(), []);
  const {
    data: subDepartments,
    loading: resolvingAccess,
    error: accessError,
    refetch: retryAccess,
  } = useApi(subDepartmentFetcher);
  const dealTrackerRole = resolveDealTrackerRole(subDepartments ?? []);
  const dealTrackerAction = dealTrackerRole === "staffer" ? DEAL_TRACKER_STAFFER : DEAL_TRACKER_BANKER;

  const generalActions = canCreateDeals ? ACTIONS : ACTIONS.filter((item) => item.action !== "create-deal");
  const actions = dealTrackerRole ? [dealTrackerAction, LAUNCH_TRIYO] : generalActions;

  const openAction = (item: QuickAction) => {
    setNotice("");
    if (item.action === "create-deal") {
      if (getCreateDealDisplay() === "dialog") {
        void openCreateDealDialog().then((result) => {
          if (result === "unavailable") setView("create-deal");
        });
        return;
      }
      setView("create-deal");
      return;
    }
    if (item.action === "weekly-hours") {
      if (getWeeklyHoursDisplay() === "dialog") {
        void openWeeklyHoursDialog().then((result) => {
          if (result === "unavailable") setView("weekly-hours");
        });
        return;
      }
      setView("weekly-hours");
      return;
    }
    if (item.action === "deal-tracker") {
      if (getDealTrackerDisplay() === "dialog") {
        void openDealTrackerDialog().then((result) => {
          if (result === "unavailable") setView("deal-tracker");
        });
        return;
      }
      setView("deal-tracker");
      return;
    }
    const launchUrl = resolveLaunchUrl();
    if (launchUrl) {
      window.open(launchUrl, "_blank", "noopener,noreferrer");
    } else {
      setNotice("The TRIYO web application URL is not configured.");
    }
  };

  if (view !== "list") {
    return (
      <div className="tab-panel">
        <TabSectionHeader
          title={view === "deal-tracker" ? dealTrackerAction.title : VIEW_TITLES[view]}
          action={
            <button type="button" className="quick-actions__back" onClick={() => setView("list")}>
              Back
            </button>
          }
        />
        {view === "create-deal" ? (
          <CreateDeal onClose={() => setView("list")} />
        ) : (
          <div className="list-scroll quick-actions__detail">
            {view === "deal-tracker" ? <DealTrackerHome onClose={() => setView("list")} /> : <WeeklyHours />}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="tab-panel">
      <TabSectionHeader title="Quick Actions" />
      {notice ? (
        <div className="quick-actions__notice" role="status" aria-live="polite">
          {notice}
        </div>
      ) : null}
      <div className="list-scroll quick-actions__list">
        {/* The shortcut list itself depends on the answer, so nothing is shown
            until it arrives -- otherwise an M&A Banker sees actions that are
            not theirs flash past. */}
        {resolvingAccess ? (
          <div className="quick-actions__notice" role="status">
            <FluentSpinner label="Checking your TRIYO shortcuts…" />
          </div>
        ) : null}
        {/* A failed lookup is not an answer: falling through to the general list
            would strip an M&A user's Deal Tracker and show shortcuts that are
            not theirs. Hold the list and offer a retry instead. */}
        {!resolvingAccess && accessError ? (
          <div className="quick-actions__notice" role="alert">
            <p>{accessError}</p>
            <button type="button" className="quick-actions__back" onClick={() => void retryAccess()}>
              Try again
            </button>
          </div>
        ) : null}
        {resolvingAccess || accessError
          ? null
          : actions.map((item) => (
              <button key={item.id} type="button" className="quick-action-row" onClick={() => openAction(item)}>
                <span className="quick-action-row__body">
                  <span className="quick-action-row__left">
                    <span className="quick-action-row__icon">{item.icon}</span>
                    <span className="quick-action-row__text">
                      <span className="quick-action-row__title">{item.title}</span>
                      <span className="quick-action-row__desc">{item.desc}</span>
                    </span>
                  </span>
                  <ChevronRightRegular className="quick-action-row__chevron" />
                </span>
                <span className="list-row__divider" />
              </button>
            ))}
      </div>
    </div>
  );
}
